import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Plus, Sparkles, Trash2, Pencil, Copy, Link2, Loader2, X, Store, Users, Ban, RotateCcw, ImagePlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  getToken, getAdmin, clearAdminSession,
  fetchVenues, createVenue, updateVenue, deleteVenue, generateProfile, adminGoogleLogin,
  fetchMembers, banMember, unbanMember, uploadVenuePhoto, fileUrl, PRIZE_KINDS, prizeEmoji,
} from "@/lib/api";
import { CATEGORIES } from "@/data/mockData";
import { toast } from "sonner";

const EMPTY = {
  name: "", type: "Pub", location: "", distance: 5, category: "general", blurb: "", photo_url: "",
  prize: { title: "", kind: "points", condition_type: "score", min_score: 4, expiry_minutes: 5 },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const admin = getAdmin();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = closed, object = open
  const [editingId, setEditingId] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [view, setView] = useState("venues"); // venues | members
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // venue pending delete

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setVenues(await fetchVenues());
    } catch {
      toast.error("Couldn't load venues.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      setMembers(await fetchMembers());
    } catch {
      toast.error("Couldn't load members.");
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    // Handle Google OAuth callback first: land at /admin#session_id=...
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      window.history.replaceState(null, "", window.location.pathname);
      if (sid) {
        adminGoogleLogin(sid)
          .then((a) => { toast.success(`Welcome, ${a.name}`); load(); })
          .catch((e) => {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Google sign-in failed.");
            navigate("/admin/login");
          });
        return;
      }
    }
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [navigate, load]);

  const openNew = () => { setForm({ ...EMPTY, prize: { ...EMPTY.prize } }); setEditingId(null); };
  const openEdit = (v) => {
    setForm({ ...EMPTY, ...v, prize: { ...EMPTY.prize, ...(v.prize || {}) } });
    setEditingId(v.id);
  };

  const setField = (k, val) => setForm((f) => ({ ...f, [k]: val }));
  const setPrize = (k, val) => setForm((f) => ({ ...f, prize: { ...f.prize, [k]: val } }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const url = await uploadVenuePhoto(file);
      setField("photo_url", url);
      toast.success("Photo uploaded!");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Photo upload failed.");
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const genProfile = async () => {
    if (!form.name) { toast.error("Add a venue name first."); return; }
    setGenLoading(true);
    try {
      const blurb = await generateProfile(form.name, form.type, form.location);
      setField("blurb", blurb);
      toast.success("AI profile generated!");
    } catch {
      toast.error("Profile generation failed.");
    } finally {
      setGenLoading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Venue name is required."); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(), type: form.type, location: form.location,
      distance: Number(form.distance) || 5, category: form.category, blurb: form.blurb, photo_url: form.photo_url || "",
      prize: { ...form.prize, min_score: Number(form.prize.min_score) || 4, expiry_minutes: Math.min(10, Math.max(1, Number(form.prize.expiry_minutes) || 5)) },
    };
    try {
      if (editingId) {
        await updateVenue(editingId, payload);
        toast.success("Venue updated.");
      } else {
        await createVenue(payload);
        toast.success("Venue created!");
      }
      setForm(null);
      setEditingId(null);
      load();
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Session expired."); navigate("/admin/login"); }
      else toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteVenue(id);
      toast.success("Venue removed.");
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const doBan = async (m) => {
    try {
      if (m.banned) {
        await unbanMember(m.id);
        toast.success(`Unbanned @${m.handle}`);
      } else {
        await banMember(m.id);
        toast.success(`Banned @${m.handle}`);
      }
      loadMembers();
    } catch {
      toast.error("Action failed.");
    }
  };

  const copyUrl = (id) => {
    const url = `${window.location.origin}/venue/${id}`;
    navigator.clipboard?.writeText(url);
    toast.success("Profile URL copied!");
  };

  const logout = () => { clearAdminSession(); navigate("/admin/login"); };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-slate-950 p-6 pb-16">
      <div className="flex items-center justify-between pt-4 mb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-purple-400">Admin Console</p>
          <h1 className="font-display text-3xl font-black text-slate-50 leading-none mt-1">Partner Venues</h1>
          {admin && <p className="text-slate-500 text-sm mt-1">{admin.email}</p>}
        </div>
        <button data-testid="admin-logout-btn" onClick={logout} className="text-slate-500 hover:text-red-400 p-2">
          <LogOut size={20} strokeWidth={2.5} />
        </button>
      </div>

      <div className="flex bg-slate-900 rounded-2xl p-1.5 mb-6 border border-slate-800">
        <button
          data-testid="admin-tab-venues"
          onClick={() => setView("venues")}
          className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 no-tap-highlight ${view === "venues" ? "bg-amber-400 text-slate-950" : "text-slate-400"}`}
        >
          <Store size={16} /> Venues
        </button>
        <button
          data-testid="admin-tab-members"
          onClick={() => { setView("members"); loadMembers(); }}
          className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 no-tap-highlight ${view === "members" ? "bg-pink-500 text-white" : "text-slate-400"}`}
        >
          <Users size={16} /> Members
        </button>
      </div>

      {view === "venues" && !form && (
        <motion.button
          data-testid="admin-add-venue-btn"
          whileTap={{ scale: 0.97 }}
          onClick={openNew}
          className="w-full h-14 bg-amber-400 text-slate-950 rounded-2xl font-display font-black text-lg flex items-center justify-center gap-2 mb-6 shadow-[0_0_20px_rgba(245,158,11,0.35)] no-tap-highlight"
        >
          <Plus strokeWidth={3} /> Add Partner Venue
        </motion.button>
      )}

      {view === "venues" && form && (
        <div data-testid="venue-form" className="bg-slate-900 rounded-2xl p-5 border border-slate-800 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-black text-xl text-slate-50">{editingId ? "Edit Venue" : "New Venue"}</h2>
            <button data-testid="venue-form-close" onClick={() => setForm(null)} className="text-slate-500 p-1"><X size={20} /></button>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Venue Name</label>
            <Input data-testid="venue-name-input" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="The Tipsy Otter" className="bg-slate-800 border-slate-700 text-slate-50 rounded-xl h-12" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Type</label>
              <Input data-testid="venue-type-input" value={form.type} onChange={(e) => setField("type", e.target.value)} placeholder="Pub" className="bg-slate-800 border-slate-700 text-slate-50 rounded-xl h-12" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Distance (mi)</label>
              <Input data-testid="venue-distance-input" type="number" value={form.distance} onChange={(e) => setField("distance", e.target.value)} className="bg-slate-800 border-slate-700 text-slate-50 rounded-xl h-12" />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Location</label>
            <Input data-testid="venue-location-input" value={form.location} onChange={(e) => setField("location", e.target.value)} placeholder="Devon, UK" className="bg-slate-800 border-slate-700 text-slate-50 rounded-xl h-12" />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Venue Photo</label>
            {form.photo_url ? (
              <div className="relative rounded-xl overflow-hidden">
                <img data-testid="venue-photo-preview" src={fileUrl(form.photo_url)} alt="Venue" className="w-full h-40 object-cover" />
                <button
                  data-testid="venue-photo-remove"
                  onClick={() => setField("photo_url", "")}
                  className="absolute top-2 right-2 bg-slate-950/80 text-white rounded-full p-1.5"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label data-testid="venue-photo-upload" className="flex flex-col items-center justify-center gap-2 h-32 border-2 border-dashed border-slate-700 rounded-xl cursor-pointer text-slate-400 bg-slate-800/40">
                {photoUploading ? <Loader2 className="animate-spin" /> : <ImagePlus size={24} />}
                <span className="text-xs font-bold">{photoUploading ? "Uploading…" : "Tap to upload a photo"}</span>
                <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" disabled={photoUploading} />
              </label>
            )}
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Quiz Category</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  data-testid={`venue-cat-${c.id}`}
                  onClick={() => setField("category", c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold no-tap-highlight ${form.category === c.id ? "bg-cyan-400 text-slate-950" : "bg-slate-800 text-slate-400"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Profile Blurb</label>
              <button
                data-testid="venue-generate-profile-btn"
                onClick={genProfile}
                disabled={genLoading}
                className="flex items-center gap-1 text-xs font-bold text-purple-400 disabled:opacity-60"
              >
                {genLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} AI Generate
              </button>
            </div>
            <Textarea data-testid="venue-blurb-input" value={form.blurb} onChange={(e) => setField("blurb", e.target.value)} rows={3} placeholder="Let AI write it, or type your own…" className="bg-slate-800 border-slate-700 text-slate-50 rounded-xl" />
          </div>

          <div className="border-t border-slate-800 pt-4">
            <p className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-3">🎁 Prize / Offer</p>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Offer Title</label>
            <Input data-testid="prize-title-input" value={form.prize.title} onChange={(e) => setPrize("title", e.target.value)} placeholder="40% off Spoilt Pig & Denhay bacon" className="bg-slate-800 border-slate-700 text-slate-50 rounded-xl h-12 mb-3" />

            <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Offer Type</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {PRIZE_KINDS.map((k) => (
                <button key={k.id} data-testid={`prize-kind-${k.id}`} onClick={() => setPrize("kind", k.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold no-tap-highlight ${form.prize.kind === k.id ? "bg-pink-500 text-white" : "bg-slate-800 text-slate-400"}`}>
                  {k.emoji} {k.label}
                </button>
              ))}
            </div>

            <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Win Condition</label>
            <div className="flex gap-2 mb-3">
              <button data-testid="prize-cond-score" onClick={() => setPrize("condition_type", "score")}
                className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold no-tap-highlight ${form.prize.condition_type === "score" ? "bg-amber-400 text-slate-950" : "bg-slate-800 text-slate-400"}`}>
                Reach a score
              </button>
              <button data-testid="prize-cond-leaderboard" onClick={() => setPrize("condition_type", "leaderboard")}
                className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold no-tap-highlight ${form.prize.condition_type === "leaderboard" ? "bg-amber-400 text-slate-950" : "bg-slate-800 text-slate-400"}`}>
                Top the leaderboard
              </button>
            </div>
            {form.prize.condition_type === "score" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Min score (out of 5)</label>
                <Input data-testid="prize-minscore-input" type="number" min={1} max={5} value={form.prize.min_score} onChange={(e) => setPrize("min_score", e.target.value)} className="bg-slate-800 border-slate-700 text-slate-50 rounded-xl h-12" />
              </div>
            )}
            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Coupon valid for (1–10 minutes)</label>
              <Input data-testid="prize-expiry-input" type="number" min={1} max={10} value={form.prize.expiry_minutes} onChange={(e) => setPrize("expiry_minutes", e.target.value)} placeholder="5" className="bg-slate-800 border-slate-700 text-slate-50 rounded-xl h-12" />
              <p className="text-[11px] text-slate-500 mt-1">Short and simple — between 1 and 10 minutes. Redeem at the bar before it expires.</p>
            </div>
          </div>

          <motion.button data-testid="venue-save-btn" whileTap={{ scale: 0.97 }} disabled={saving} onClick={save}
            className="w-full h-14 bg-green-500 text-white rounded-2xl font-display font-black text-lg flex items-center justify-center gap-2 disabled:opacity-70 no-tap-highlight">
            {saving ? <Loader2 className="animate-spin" /> : (editingId ? "Update Venue" : "Create Venue")}
          </motion.button>
        </div>
      )}

      {view === "venues" && (loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-600" /></div>
      ) : (
        <div className="space-y-4" data-testid="admin-venue-list">
          {venues.length === 0 && !form && (
            <div className="text-center py-10 text-slate-500">
              <Store size={40} className="mx-auto mb-3 opacity-40" />
              No partner venues yet. Add your first one!
            </div>
          )}
          {venues.map((v) => (
            <div key={v.id} data-testid={`admin-venue-${v.id}`} className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {v.photo_url && (
                    <img
                      data-testid={`venue-thumb-${v.id}`}
                      src={fileUrl(v.photo_url)}
                      alt={v.name}
                      className="w-14 h-14 rounded-xl object-cover shrink-0 border border-slate-700"
                    />
                  )}
                  <div className="min-w-0">
                    <h3 className="font-display font-bold text-lg text-slate-50 truncate">{v.name}</h3>
                    <p className="text-slate-400 text-sm">{v.type}{v.location ? ` · ${v.location}` : ""} · {v.distance} mi</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`venue-edit-${v.id}`} onClick={() => openEdit(v)} className="text-slate-400 hover:text-cyan-400 p-2"><Pencil size={16} /></button>
                  <button data-testid={`venue-delete-${v.id}`} onClick={() => setConfirmDel(v)} className="text-slate-400 hover:text-red-400 p-2"><Trash2 size={16} /></button>
                </div>
              </div>
              {v.blurb && <p className="text-slate-300 text-sm mt-2 leading-relaxed">{v.blurb}</p>}
              {v.prize?.title && (
                <div className="mt-3 inline-flex items-center gap-1.5 bg-amber-400/10 border border-amber-400/30 rounded-full px-3 py-1 text-xs font-bold text-amber-300">
                  {prizeEmoji(v.prize.kind)} {v.prize.title}
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <button data-testid={`venue-copy-${v.id}`} onClick={() => copyUrl(v.id)} className="flex items-center gap-1.5 text-xs font-bold text-slate-300 bg-slate-800 rounded-full px-3 py-2">
                  <Copy size={13} /> Copy URL
                </button>
                <button data-testid={`venue-view-${v.id}`} onClick={() => navigate(`/venue/${v.id}`)} className="flex items-center gap-1.5 text-xs font-bold text-slate-300 bg-slate-800 rounded-full px-3 py-2">
                  <Link2 size={13} /> View Profile
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {view === "members" && (membersLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-600" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-member-list">
          {members.length === 0 && (
            <div className="text-center py-10 text-slate-500">
              <Users size={40} className="mx-auto mb-3 opacity-40" />
              No members yet. Players appear here after they log in.
            </div>
          )}
          {members.map((m) => (
            <div key={m.id} data-testid={`admin-member-${m.id}`} className={`bg-slate-900 rounded-2xl p-4 border flex items-center gap-3 ${m.banned ? "border-red-500/40" : "border-slate-800"}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black ${m.banned ? "bg-red-500/20 text-red-400" : "bg-slate-800 text-slate-300"}`}>
                {(m.name || m.handle || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-100 truncate">{m.name || `@${m.handle}`}</div>
                <div className="text-slate-500 text-xs truncate">@{m.handle}{m.banned ? " · BANNED" : ""}</div>
              </div>
              <button
                data-testid={`member-ban-${m.id}`}
                onClick={() => doBan(m)}
                className={`flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-2 no-tap-highlight ${m.banned ? "bg-slate-800 text-green-400" : "bg-red-500 text-white"}`}
              >
                {m.banned ? <><RotateCcw size={13} /> Unban</> : <><Ban size={13} /> Ban</>}
              </button>
            </div>
          ))}
        </div>
      ))}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="delete-venue-dialog" className="bg-slate-900 border-slate-800 text-slate-50 max-w-sm rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-slate-50">Remove this pub?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {confirmDel ? `"${confirmDel.name}" and its prize will be permanently deleted. This can't be undone.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-venue-cancel" className="bg-slate-800 border-slate-700 text-slate-100 hover:bg-slate-700 rounded-xl">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-venue-confirm" onClick={() => confirmDel && remove(confirmDel.id)} className="bg-red-500 text-white hover:bg-red-600 rounded-xl">Delete pub</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
